import React from "react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import Hero from "@/components/Hero";
import ContactForm from "@/components/ContactForm";
import SEO from "@/components/SEO";
import {
    PARTNERS,
    AREAS_HOME,
    ABOUT_TEXT,
    SOCIAL_RESPONSIBILITY,
    TESTIMONIALS,
    PLANS,
    TEAM,
    SITE,
} from "@/data/site";
import {
    ArrowRight,
    Facebook,
    Instagram,
    Linkedin,
    Handshake,
    Scale,
    Star,
    MapPin,
    Phone,
    Clock,
    ShieldCheck,
    Globe,
    BadgeCheck,
    CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ eyebrow, title, children, id, className = "" }) => (
    <section id={id} className={`py-20 md:py-24 px-6 ${className}`}>
        <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
                {eyebrow && <span className="acd-eyebrow">{eyebrow}</span>}
                {title && (
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold max-w-3xl mx-auto">
                        {title}
                    </h2>
                )}
            </div>
            {children}
        </div>
    </section>
);

const Partnerships = () => (
    <Section eyebrow="Parcerias" title="Uma Rede Global de Especialistas" className="bg-[var(--acd-bg-soft)]">
        <div className="grid md:grid-cols-2 gap-6">
            {PARTNERS.map((p) => (
                <div key={p.title} className="acd-partner-card" data-testid={`partner-${p.title.substring(0, 12)}`}>
                    <div className="flex items-start gap-4">
                        <div className="shrink-0 w-12 h-12 rounded-lg bg-white/15 flex items-center justify-center">
                            {p.title.includes("África") ? (
                                <Scale size={22} />
                            ) : (
                                <Handshake size={22} />
                            )}
                        </div>
                        <div>
                            <h3 className="text-lg font-bold mb-2 text-white">
                                {p.title}
                            </h3>
                            <p className="text-white/85 text-sm leading-relaxed">
                                {p.desc}
                            </p>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </Section>
);

const Traduz24Section = () => {
    const chips = [
        { icon: Clock, label: "Entrega em 24h" },
        { icon: ShieldCheck, label: "Certificado por Advogado" },
        { icon: Globe, label: "PT · EN · FR · ES" },
        { icon: BadgeCheck, label: "100% Válido Legalmente" },
        { icon: CheckCircle2, label: "€0.10 por Palavra" },
    ];

    return (
        <section className="py-16 md:py-20 px-6 bg-white">
            <div className="max-w-7xl mx-auto">
                <div className="rounded-3xl border border-gray-200 bg-[var(--acd-bg-soft)] overflow-hidden shadow-sm">
                    <div className="grid lg:grid-cols-[auto_1fr] gap-0">

                        {/* Left accent bar + logo panel */}
                        <div className="flex flex-col items-center justify-center gap-5 px-10 py-12 bg-white border-b lg:border-b-0 lg:border-r border-gray-200 min-w-[220px]">
                            <span className="text-xs font-bold uppercase tracking-widest text-[var(--acd-gold)] mb-1">
                                Parceiro Oficial
                            </span>
                            <a href="https://traduz24.com" target="_blank" rel="noreferrer" aria-label="Traduz24">
                                <img
                                    src="https://traduz24.com/logo.png"
                                    alt="Traduz24 — Traduções Certificadas"
                                    className="h-14 w-auto object-contain"
                                    onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display = "flex";
                                    }}
                                />
                                <div
                                    className="hidden items-center justify-center font-black text-2xl tracking-tight"
                                    style={{ display: "none" }}
                                >
                                    <span style={{ color: "#1a6fdb" }}>traduz</span><span style={{ color: "#111111" }}>24</span>
                                </div>
                            </a>
                            <span className="text-xs text-gray-400 text-center leading-relaxed max-w-[160px]">
                                traduz24.com
                            </span>
                        </div>

                        {/* Right: content */}
                        <div className="p-8 md:p-10 lg:p-12">
                            <h2 className="text-2xl md:text-3xl font-bold text-[var(--acd-navy-900)] mb-3 leading-tight">
                                Traduções Certificadas em 24 Horas
                            </h2>
                            <p className="text-[color:var(--acd-muted)] leading-relaxed mb-7 max-w-2xl">
                                Em parceria com a <span className="font-semibold text-[var(--acd-navy-900)]">Traduz24</span>,
                                oferecemos um serviço completo de traduções certificadas por advogado,
                                com validade legal imediata em Portugal e internacionalmente. 100% online,
                                sem necessidade de deslocações — faça upload do documento, pague de forma
                                segura e receba a tradução por email.
                            </p>

                            {/* Feature chips */}
                            <div className="flex flex-wrap gap-2 mb-8">
                                {chips.map(({ icon: Icon, label }) => (
                                    <span
                                        key={label}
                                        className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-[var(--acd-navy-900)] text-xs font-medium px-3 py-1.5 rounded-full shadow-sm"
                                    >
                                        <Icon size={13} className="text-[var(--acd-gold)]" />
                                        {label}
                                    </span>
                                ))}
                            </div>

                            <a
                                href="https://traduz24.com"
                                target="_blank"
                                rel="noreferrer"
                                className="acd-btn-gradient inline-flex items-center gap-2"
                                data-testid="traduz24-cta-btn"
                            >
                                Aceder ao Serviço de Tradução
                                <ArrowRight size={15} />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const AreasSection = () => (
    <Section
        eyebrow="Áreas de Atuação"
        title="O rigor jurídico aliado à visão estratégica. A proteção dos seus interesses começa aqui."
    >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {AREAS_HOME.map((a) => (
                <article key={a.title} className="acd-card" data-testid={`area-card-${a.anchor}`}>
                    <div
                        className="w-full h-56 bg-cover bg-center"
                        style={{ backgroundImage: `url(${a.image})` }}
                    />
                    <div className="p-6">
                        <h3 className="text-xl font-bold mb-3 text-[var(--acd-navy-900)]">
                            {a.title}
                        </h3>
                        <p className="text-[color:var(--acd-muted)] text-sm mb-5 leading-relaxed">
                            {a.text}
                        </p>
                        <Link
                            to={`/areas-de-atuacao#${a.anchor}`}
                            className="acd-btn-gradient text-xs"
                        >
                            Mais informações
                        </Link>
                    </div>
                </article>
            ))}
        </div>
        <div className="mt-12 text-center">
            <Link to="/areas-de-atuacao" className="acd-btn-outline" data-testid="more-areas-btn">
                Mais áreas de atuação
            </Link>
        </div>
    </Section>
);

const AboutSection = () => {
    const renderPara = (text) => {
        // Very small markdown-like bold parser
        const parts = text.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((p, i) =>
            p.startsWith("**") && p.endsWith("**") ? (
                <strong key={i}>{p.slice(2, -2)}</strong>
            ) : (
                <span key={i}>{p}</span>
            ),
        );
    };

    return (
        <section className="py-20 md:py-24 px-6 bg-[var(--acd-bg-soft)]" id="sobre-nos">
            <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
                <div>
                    <div className="rounded-2xl overflow-hidden shadow-lg">
                        <img
                            src="/images/wp/2026/04/Ana-Cristina-Advocacia-Portugal.jpg"
                            alt="Dra. Ana Cristina Domingues"
                            className="w-full h-auto"
                        />
                    </div>
                    <div className="flex items-center justify-center mt-8">
                        <img
                            src={SITE.logoUrl}
                            alt="ACD Advogados logo"
                            className="h-24"
                        />
                    </div>
                </div>
                <div>
                    <span className="acd-eyebrow">Sobre Nós</span>
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                        {ABOUT_TEXT.title}
                    </h2>
                    {ABOUT_TEXT.paragraphs.map((p, idx) => (
                        <p
                            key={idx}
                            className="text-[color:var(--acd-muted)] mb-4 leading-relaxed"
                        >
                            {renderPara(p)}
                        </p>
                    ))}
                    <ul className="space-y-3 my-5 list-disc list-outside pl-5">
                        {ABOUT_TEXT.bullets.map((b) => (
                            <li
                                key={b}
                                className="text-[color:var(--acd-muted)] leading-relaxed"
                            >
                                {b}
                            </li>
                        ))}
                    </ul>
                    {ABOUT_TEXT.closing.map((p, idx) => (
                        <p
                            key={idx}
                            className="text-[color:var(--acd-muted)] mb-4 leading-relaxed"
                        >
                            {p}
                        </p>
                    ))}

                    <div className="grid grid-cols-4 gap-4 mt-8 text-center">
                        {[
                            { n: "30+", l: "Anos de Experiência" },
                            { n: "5K+", l: "Processos" },
                            { n: "10K+", l: "Clientes Satisfeitos" },
                            { n: "20+", l: "Colaboradores" },
                        ].map((s) => (
                            <div key={s.l}>
                                <div className="text-3xl font-bold text-[var(--acd-navy-900)]">
                                    {s.n}
                                </div>
                                <div className="text-xs text-[color:var(--acd-muted)] mt-1">
                                    {s.l}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

const SocialResponsibility = () => (
    <section className="relative py-20 md:py-24 px-6 bg-[var(--acd-navy-900)] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('https://customer-assets.emergentagent.com/job_advogados-staging/artifacts/l3ac462n_pexels-phoze-20738549.webp')] bg-cover bg-center" />
        <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-14">
                <span className="acd-eyebrow">Responsabilidade Social</span>
                <h2 className="text-3xl md:text-4xl font-bold text-white max-w-3xl mx-auto">
                    Responsabilidade Social
                </h2>
                <p className="text-white/85 mt-5 max-w-3xl mx-auto">
                    A responsabilidade social faz parte da cultura de Ana
                    Cristina Domingues Advogados, assumindo perante a comunidade
                    a prestação de serviços jurídicos em benefício de
                    instituições e indivíduos carenciados, no acesso ao Direito
                    e à Justiça.
                </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
                {SOCIAL_RESPONSIBILITY.map((s) => (
                    <a
                        key={s.title}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="bg-white/5 hover:bg-white/10 transition p-6 rounded-2xl border border-white/10 backdrop-blur"
                        data-testid={`social-${s.title.substring(0, 10)}`}
                    >
                        <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center mb-4 p-2">
                            <img
                                src={s.logo}
                                alt={s.title}
                                className="max-h-full max-w-full object-contain"
                            />
                        </div>
                        <h3 className="font-bold text-white text-lg mb-3">
                            {s.title}
                        </h3>
                        <p className="text-white/80 text-sm leading-relaxed">
                            {s.desc}
                        </p>
                    </a>
                ))}
            </div>
        </div>
    </section>
);

const Testimonials = () => (
    <Section eyebrow="Avaliações Reais" title="A Segurança de Estar Bem Representado" className="bg-[var(--acd-bg-soft)]">
        <div className="grid md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t, idx) => (
                <div
                    key={t.name}
                    className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100"
                    data-testid={`testimonial-${idx}`}
                >
                    <div className="flex gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                            <Star
                                key={i}
                                size={16}
                                className="fill-[var(--acd-gold)] text-[var(--acd-gold)]"
                            />
                        ))}
                    </div>
                    <p className="text-[color:var(--acd-muted)] leading-relaxed mb-4 italic">
                        &ldquo;{t.text}&rdquo;
                    </p>
                    <div className="font-bold text-[var(--acd-navy-900)]">
                        {t.name}
                    </div>
                </div>
            ))}
        </div>
    </Section>
);

const Plans = () => (
    <Section
        eyebrow="Planos de Assessoria"
        title="Previsibilidade e segurança, sem surpresas."
        id="planos"
    >
        <p className="text-center text-lg text-[color:var(--acd-muted)] max-w-3xl mx-auto -mt-10 mb-14">
            Assessoria jurídica transparente, garantindo clareza, consistência e
            total controlo para si ou para o seu negócio.
        </p>
        <div className="grid md:grid-cols-3 gap-8">
            {PLANS.map((p) => (
                <div
                    key={p.name}
                    className={`relative rounded-2xl p-8 border ${
                        p.featured
                            ? "border-[var(--acd-blue-600)] bg-[var(--acd-navy-900)] text-white shadow-2xl scale-[1.02]"
                            : "border-gray-200 bg-white"
                    }`}
                    data-testid={`plan-${p.name.replace(/\s/g, "-").toLowerCase()}`}
                >
                    {p.badge && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--acd-orange)] text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider">
                            {p.badge}
                        </span>
                    )}
                    <h3
                        className={`text-2xl font-bold mb-1 ${
                            p.featured ? "text-white" : "text-[var(--acd-navy-900)]"
                        }`}
                    >
                        {p.name}
                    </h3>
                    <p
                        className={`text-sm mb-4 ${
                            p.featured ? "text-white/70" : "text-[color:var(--acd-muted)]"
                        }`}
                    >
                        {p.subtitle}
                    </p>
                    <div className="mb-6 flex items-baseline gap-1">
                        <span
                            className={`text-5xl font-bold ${
                                p.featured
                                    ? "text-white"
                                    : "text-[var(--acd-navy-900)]"
                            }`}
                        >
                            {p.price}
                        </span>
                        <span
                            className={`${
                                p.featured
                                    ? "text-white/70"
                                    : "text-[color:var(--acd-muted)]"
                            }`}
                        >
                            {p.period}
                        </span>
                    </div>
                    <h4
                        className={`text-xs font-bold uppercase tracking-widest mb-4 ${
                            p.featured ? "text-[var(--acd-gold)]" : "text-[var(--acd-navy-900)]"
                        }`}
                    >
                        Lista de Benefícios
                    </h4>
                    <ul className="space-y-2 mb-8">
                        {p.benefits.map((b) => (
                            <li
                                key={b}
                                className={`flex items-start gap-2 text-sm leading-relaxed ${
                                    p.featured
                                        ? "text-white/90"
                                        : "text-[color:var(--acd-muted)]"
                                }`}
                            >
                                <ArrowRight
                                    size={14}
                                    className="mt-1 shrink-0 text-[var(--acd-gold)]"
                                />
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                    <a
                        href={p.cta}
                        target="_blank"
                        rel="noreferrer"
                        className="acd-btn-gradient w-full justify-center"
                    >
                        Mais informações
                    </a>
                    <p
                        className={`text-xs mt-4 ${
                            p.featured ? "text-white/60" : "text-[color:var(--acd-muted)]"
                        }`}
                    >
                        {p.note}
                    </p>
                </div>
            ))}
        </div>
    </Section>
);

const OfficesAndCTA = () => (
    <section className="py-20 md:py-24 px-6 bg-[var(--acd-navy-900)] text-white">
        <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
                <span className="acd-eyebrow">Fale Connosco</span>
                <h2 className="text-3xl md:text-4xl font-bold text-white max-w-3xl mx-auto">
                    Estamos perto de si. Agende uma reunião com a nossa equipa.
                </h2>
                <p className="text-white/80 mt-4 max-w-3xl mx-auto">
                    Disponibilizamos atendimento presencial e personalizado em
                    Lisboa e nos Açores. Entre em contacto e confie a resolução
                    dos seus desafios jurídicos a profissionais experientes e
                    focados em resultados.
                </p>
                <a
                    href={SITE.whatsappUrlAppointment}
                    target="_blank"
                    rel="noreferrer"
                    className="acd-btn-gradient mt-8"
                    data-testid="agendar-atendimento"
                >
                    Agendar Atendimento
                </a>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mt-10">
                {SITE.offices.map((o) => {
                    const slug = o.city.toLowerCase().includes("lisboa")
                        ? "lisboa"
                        : "ponta-delgada";
                    return (
                        <Link
                            to={`/advogado-${slug}`}
                            key={o.city}
                            className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur hover:bg-white/10 hover:border-white/30 transition-all group cursor-pointer"
                            data-testid={`office-card-${slug}`}
                        >
                            <span className="acd-eyebrow">{o.region}</span>
                            <h3 className="text-xl font-bold text-white mb-4 group-hover:text-[var(--acd-gold)] transition-colors">
                                {o.city}
                            </h3>
                            <div className="flex items-start gap-3 mb-4 text-white/85">
                                <MapPin size={18} className="mt-1 shrink-0" />
                                <span>{o.address}</span>
                            </div>
                            <div className="flex items-center gap-3 text-white/85">
                                <Phone size={18} className="shrink-0" />
                                <span>
                                    <strong>Telefones:</strong>{" "}
                                    {o.phones.join(" | ")}
                                </span>
                            </div>
                            <p className="text-xs text-white/60 mt-2">{o.note}</p>
                            <span className="inline-flex items-center gap-2 text-sm font-bold text-[var(--acd-gold)] mt-5 group-hover:gap-3 transition-all">
                                Ver escritório <ArrowRight size={14} />
                            </span>
                        </Link>
                    );
                })}
            </div>
        </div>
    </section>
);

const TeamSection = () => (
    <Section eyebrow="Equipa" title="Especialistas comprometidos com a sua segurança jurídica" id="equipa">
        <p className="text-center text-[color:var(--acd-muted)] max-w-3xl mx-auto -mt-8 mb-14 text-lg">
            Entre os escritórios de Lisboa e Ponta Delgada, a ACD Advogados conta com uma equipa de <strong className="text-[var(--acd-navy-900)]">mais de 20 colaboradores</strong>, com especialistas nas mais diversas áreas do Direito. Abaixo apresentamos os três principais responsáveis.
        </p>
        <div className="grid md:grid-cols-3 gap-8">
            {TEAM.map((m) => (
                <article key={m.name} className="acd-card overflow-hidden">
                    <div
                        className="w-full h-80 bg-cover bg-center"
                        style={{ backgroundImage: `url(${m.photo})` }}
                    />
                    <div className="p-6">
                        <h3 className="text-lg font-bold text-[var(--acd-navy-900)]">
                            {m.name}
                        </h3>
                        <p className="text-sm text-[color:var(--acd-muted)] mt-1 mb-4">
                            {m.role}
                        </p>
                        <div className="flex gap-2">
                            <a
                                href={SITE.social.facebook}
                                target="_blank"
                                rel="noreferrer"
                                className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-[var(--acd-navy-900)] hover:bg-[var(--acd-navy-900)] hover:text-white transition"
                            >
                                <Facebook size={14} />
                            </a>
                            <a
                                href={SITE.social.instagram}
                                target="_blank"
                                rel="noreferrer"
                                className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-[var(--acd-navy-900)] hover:bg-[var(--acd-navy-900)] hover:text-white transition"
                            >
                                <Instagram size={14} />
                            </a>
                            <a
                                href={SITE.social.linkedin}
                                target="_blank"
                                rel="noreferrer"
                                className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-[var(--acd-navy-900)] hover:bg-[var(--acd-navy-900)] hover:text-white transition"
                            >
                                <Linkedin size={14} />
                            </a>
                        </div>
                    </div>
                </article>
            ))}
        </div>
    </Section>
);

const ContactSection = () => (
    <Section
        eyebrow="Contacto"
        title="Contacte-nos"
        id="contactos"
        className="bg-[var(--acd-bg-soft)]"
    >
        <div className="grid lg:grid-cols-2 gap-14 items-start">
            <div>
                <p className="text-lg text-[color:var(--acd-muted)] leading-relaxed mb-6">
                    Precisa da nossa ajuda? Pode enviar uma pequena mensagem e
                    faremos a análise do seu pedido o mais breve possível.
                </p>
                <div className="space-y-6">
                    {SITE.offices.map((o) => (
                        <div
                            key={o.city}
                            className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm"
                        >
                            <span className="acd-eyebrow">{o.region}</span>
                            <h3 className="text-lg font-bold text-[var(--acd-navy-900)] mb-3">
                                {o.city}
                            </h3>
                            <div className="flex items-start gap-3 text-sm text-[color:var(--acd-muted)] mb-2">
                                <MapPin size={16} className="mt-1 shrink-0" />
                                <span>{o.address}</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-[color:var(--acd-muted)]">
                                <Phone size={14} className="shrink-0" />
                                <span>{o.phones.join(" | ")}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                <ContactForm />
            </div>
        </div>
    </Section>
);

const LatestArticles = () => {
    const [articles, setArticles] = useState([]);
    useEffect(() => {
        axios
            .get(`${API}/articles`)
            .then((r) => setArticles(r.data.slice(0, 3)))
            .catch(() => {});
    }, []);
    if (!articles.length) return null;

    return (
        <Section eyebrow="Artigos" title="Informação Jurídica que Transforma Dúvidas em Segurança">
            <div className="grid md:grid-cols-3 gap-8">
                {articles.map((a) => (
                    <Link
                        key={a.slug}
                        to={`/artigos/${a.slug}`}
                        className="acd-card block"
                        data-testid={`home-article-${a.slug.substring(0, 20)}`}
                    >
                        <div
                            className="w-full h-52 bg-cover bg-center"
                            style={{ backgroundImage: `url(${a.image})` }}
                        />
                        <div className="p-6">
                            <span className="acd-eyebrow" style={{ color: "var(--acd-blue-600)" }}>
                                {a.category}
                            </span>
                            <h3 className="text-lg font-bold text-[var(--acd-navy-900)] mb-3 leading-snug">
                                {a.title}
                            </h3>
                            <p className="text-sm text-[color:var(--acd-muted)] mb-4 line-clamp-3">
                                {a.excerpt}
                            </p>
                            <span className="inline-flex items-center gap-2 text-sm font-bold text-[var(--acd-blue-600)]">
                                Ler Mais <ArrowRight size={14} />
                            </span>
                            <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-[color:var(--acd-muted)] flex justify-between">
                                <span>{a.author}</span>
                                <span>{a.date_display}</span>
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
            <div className="mt-12 text-center">
                <Link to="/artigos" className="acd-btn-outline">
                    Mais artigos
                </Link>
            </div>
        </Section>
    );
};

const Home = () => {
    return (
        <div data-testid="page-home">
            <SEO
                title="Início"
                description="ACD Advogados - Ana Cristina Domingues. Assessoria jurídica de excelência desde 1995 em Lisboa e Açores. Direito Penal, Civil, Trabalho, Comercial, Fiscal, Imigração e Nacionalidade. Planos de avença a partir de 79€/mês."
                canonicalPath="/"
            />
            <Hero />
            <Partnerships />
            <Traduz24Section />
            <AreasSection />
            <AboutSection />
            <SocialResponsibility />
            <Testimonials />
            <Plans />
            <OfficesAndCTA />
            <TeamSection />
            <ContactSection />
            <LatestArticles />
        </div>
    );
};

export default Home;
